import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  adminMessageListSchema,
  adminMessagePreviewSchema,
  type AdminMessageList,
  type AdminMessagePreview,
} from '../schemas';
import { useToken } from './use-token';

export type AdminMessageStatus = 'enabled' | 'disabled' | '';
export type AdminMessageScope = 'fallback' | 'targeted' | '';

export type AdminMessagesParams = {
  page?: number;
  limit?: number;
  search?: string;
  /** Catégorie du catalogue (`google-review`…), vide = toutes. */
  catalogKey?: string;
  status?: AdminMessageStatus;
  scope?: AdminMessageScope;
};

/** Modèles de messages créés par les cabinets, pour le back-office. */
export function useAdminMessages({
  page = 1,
  limit = 50,
  search = '',
  catalogKey = '',
  status = '',
  scope = '',
}: AdminMessagesParams = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (search) query.set('search', search);
  if (catalogKey) query.set('catalogKey', catalogKey);
  if (status) query.set('status', status);
  if (scope) query.set('scope', scope);

  return useQuery({
    queryKey: queryKeys.adminMessages.list({ page, limit, search, catalogKey, status, scope }),
    queryFn: () =>
      api.get<AdminMessageList>(`/admin/messages?${query}`, { schema: adminMessageListSchema }),
    enabled: token !== null,
    placeholderData: keepPreviousData,
  });
}

/** Un modèle de cabinet tel que le patient le recevra. Chargé à l'ouverture de l'aperçu. */
export function useAdminMessagePreview(id: string | null) {
  const api = useApi();
  const token = useToken();

  return useQuery({
    queryKey: queryKeys.adminMessages.preview(id ?? ''),
    queryFn: () =>
      api.get<AdminMessagePreview>(`/admin/messages/${id}/preview`, {
        schema: adminMessagePreviewSchema,
      }),
    enabled: token !== null && id !== null,
  });
}

/**
 * Allume ou éteint le message d'un cabinet. Allumer est refusé (409) tant que la
 * catégorie n'a pas ce qu'il lui faut : le message d'erreur dit quoi.
 */
export function useSetAdminMessageEnabled() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<{ id: string; enabled: boolean }>(`/admin/messages/${id}/enabled`, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'messages'] }),
  });
}
